import { z } from 'zod';
import { SYSTEM_STATUS_CODES } from '../constants/enums';
import { uuidSchema } from './common';

export const dictionaryFontWeightSchema = z.union([
  z.literal(400), z.literal(500), z.literal(600), z.literal(700), z.literal(800),
]);
export type DictionaryFontWeight = z.infer<typeof dictionaryFontWeightSchema>;

/** NULL/отсутствие означает прежний стиль, без нового переопределения. */
export const dictionaryTextStyleSchema = z.object({
  textColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Цвет задаётся в формате #RRGGBB').nullable().optional(),
  fontWeight: dictionaryFontWeightSchema.nullable().optional(),
});

/** Порядок передаётся для полного справочника, включая несколько тысяч городов. */
export const MAX_DICTIONARY_REORDER_ITEMS = 20_000;
export const dictionaryReorderSchema = z.object({
  ids: z.array(uuidSchema).min(1).max(MAX_DICTIONARY_REORDER_ITEMS),
});
/** Общий цвет для городов без своего цвета. */
export const DEFAULT_CITY_COLOR = '#94a3b8';
export const cityDefaultColorSchema = z.object({
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Цвет задаётся в формате #RRGGBB'),
}).strict();

/**
 * Справочники. Все имеют is_active вместо удаления: значение, которым
 * когда-то пользовались, нельзя стереть, не сломав исторические строки.
 */

/** Код неизменяемый: по нему связаны города и гражданство кандидатов. */
export const countryCodeSchema = z.string().regex(/^[A-Z]{2}$/, 'Код страны — две заглавные латинские буквы');
export const countryCreateSchema = z.object({
  ...dictionaryTextStyleSchema.shape,
  /** Общая жирность городов этой страны без собственного переопределения. */
  cityFontWeight: dictionaryFontWeightSchema.nullable().optional(),
  code: countryCodeSchema,
  name: z.string().trim().min(1, 'Укажите название страны').max(120),
}).strict();
export type CountryItem = z.infer<typeof countryCreateSchema>;
export const countryUpdateSchema = countryCreateSchema.omit({ code: true }).partial().strict();
export type CountryUpdate = z.infer<typeof countryUpdateSchema>;
export const citizenshipSchema = z.union([countryCodeSchema, z.literal('OTHER')]);

export const cityItemSchema = z.object({
  ...dictionaryTextStyleSchema.shape,
  id: uuidSchema,
  name: z.string(),
  color: z.string(),
  /** NULL — город наследует общий цвет. */
  colorOverride: z.string().nullable().optional(),
  country: countryCodeSchema,
  region: z.string().nullable(),
  isActive: z.boolean(),
});
export type CityItem = z.infer<typeof cityItemSchema>;

/**
 * Запрос списка городов для каскадного комбобокса (решение Р-08).
 * `country` приходит из гражданства в той же строке; если гражданство
 * не заполнено — параметр не передаётся и возвращаются города всех стран
 * с пометкой страны.
 */
export const citySearchQuerySchema = z.object({
  q: z.string().trim().max(64).optional(),
  country: countryCodeSchema.optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  offset: z.coerce.number().int().min(0).max(2_147_483_647).default(0),
  /** Всегда включить в выдачу конкретный город — тот, что уже стоит в ячейке */
  includeId: uuidSchema.optional(),
});
export type CitySearchQuery = z.infer<typeof citySearchQuerySchema>;

export const citySearchResponseSchema = z.object({
  items: z.array(cityItemSchema),
  /** Смещение следующей страницы без учёта дополнительно включённого includeId. */
  nextOffset: z.number().int().nonnegative().nullable(),
});
export type CitySearchResponse = z.infer<typeof citySearchResponseSchema>;

export const statusItemSchema = z.object({
  ...dictionaryTextStyleSchema.shape,
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  color: z.string(),
  textColor: z.string(),
  sortOrder: z.number().int(),
  /** Терминальный статус — кандидат выбыл из воронки */
  isTerminal: z.boolean(),
  isActive: z.boolean(),
});
export type StatusItem = z.infer<typeof statusItemSchema>;

export const WORK_OBJECT_DISPLAY_STYLES = ['none', 'fill', 'dot', 'border'] as const;
export const workObjectDisplayStyleSchema = z.enum(WORK_OBJECT_DISPLAY_STYLES);
export type WorkObjectDisplayStyle = z.infer<typeof workObjectDisplayStyleSchema>;

export const workObjectItemSchema = z.object({
  ...dictionaryTextStyleSchema.shape,
  id: uuidSchema,
  name: z.string(),
  region: z.string().nullable(),
  qualifier: z.string().optional(),
  selectLabel: z.string().nullable().optional(),
  displayColor: z.string().nullable().optional(),
  displayStyle: workObjectDisplayStyleSchema.optional(),
  isActive: z.boolean(),
});

export const travelGroupItemSchema = z.object({
  ...dictionaryTextStyleSchema.shape,
  id: uuidSchema,
  name: z.string(),
  color: z.string(),
  departureDate: z.string().nullable(),
  note: z.string().nullable(),
  isActive: z.boolean(),
  /** Сколько кандидатов в группе — показывается в комбобоксе */
  membersCount: z.number().int().nonnegative().optional(),
});
export type TravelGroupItem = z.infer<typeof travelGroupItemSchema>;

export const tagItemSchema = z.object({
  ...dictionaryTextStyleSchema.shape,
  id: uuidSchema,
  name: z.string(),
  color: z.string(),
  isActive: z.boolean(),
});

export const sourceItemSchema = z.object({
  ...dictionaryTextStyleSchema.shape,
  id: uuidSchema,
  name: z.string(),
  isActive: z.boolean(),
});

export const dictionaryCreateSchema = z.object({
  ...dictionaryTextStyleSchema.shape,
  name: z.string().trim().min(1).max(120),
  /** Только для городов */
  country: countryCodeSchema.optional(),
  region: z.string().trim().max(120).optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  departureDate: z.string().optional(),
  note: z.string().trim().max(500).optional(),
});

export const dictionaryUpdateSchema = dictionaryCreateSchema.partial().extend({
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
  isTerminal: z.boolean().optional(),
});

/** Стартовый набор статусов — ровно те, что были в Google-таблице, плюс «Новый». */
export const DEFAULT_STATUSES: ReadonlyArray<{
  code: (typeof SYSTEM_STATUS_CODES)[number] | 'blacklist' | 'remote' | 'office' | 'registered';
  name: string;
  color: string;
  textColor: string;
  sortOrder: number;
  isTerminal: boolean;
}> = [
  { code: 'new', name: 'Новый', color: '#e5e7eb', textColor: '#111827', sortOrder: 10, isTerminal: false },
  { code: 'no_answer', name: 'Не отвечает', color: '#dc2626', textColor: '#ffffff', sortOrder: 20, isTerminal: false },
  { code: 'refused', name: 'Отказ', color: '#b91c1c', textColor: '#ffffff', sortOrder: 30, isTerminal: true },
  { code: 'postponed', name: 'Перенос', color: '#fde68a', textColor: '#78350f', sortOrder: 40, isTerminal: false },
  { code: 'confirmed', name: 'Подтвердил', color: '#15803d', textColor: '#ffffff', sortOrder: 50, isTerminal: false },
  { code: 'in_car', name: 'В машине', color: '#c4b5fd', textColor: '#3b0764', sortOrder: 60, isTerminal: false },
  // Ниже — статусы, которые обнаружились в рабочей таблице за 2026 год.
  // Кода в SYSTEM_STATUS_CODES у них намеренно нет: «Дистанционно» и «Офис» —
  // это форма занятости, а не ступень воронки, и заказчик вправе их отключить.
  // Стабильные коды всё же заданы: по ним сопоставляет импорт.
  { code: 'blacklist', name: 'ЧС', color: '#1f2937', textColor: '#ffffff', sortOrder: 35, isTerminal: true },
  { code: 'remote', name: 'Дистанционно', color: '#bfdbfe', textColor: '#1e3a8a', sortOrder: 70, isTerminal: false },
  { code: 'office', name: 'Офис', color: '#bbf7d0', textColor: '#14532d', sortOrder: 80, isTerminal: false },
  { code: 'registered', name: 'Оформился (-ась)', color: '#008000', textColor: '#ffffff', sortOrder: 90, isTerminal: false },
];
